import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAdminUserActions } from 'pricing/hooks';
import { IPricingProfileAdminUser } from 'pricing/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  pricingProfileAdminUsers: IPricingProfileAdminUser[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
  notificationPosition?: NotificationPosition;
}

export const RemovePricingProfileAdminUserButton = ({
  pricingProfileAdminUsers,
  buttonElement,
  buttonLabel,
  onRemoveSuccess,
  disabled = false,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { removeAdminUsers } = useAdminUserActions({
    selectionCount: pricingProfileAdminUsers.length,
    position: notificationPosition,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const removedAdminUserIds = pricingProfileAdminUsers.map((adminUser) => adminUser.id);

    const { success } = await removeAdminUsers({
      ids: removedAdminUserIds,
    });

    if (!success) {
      return;
    }

    onRemoveSuccess?.();
  }, [onRemoveSuccess, pricingProfileAdminUsers, removeAdminUsers]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, buttonLabel, disabled, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemovePricingProfileAdminUser', {
          count: pricingProfileAdminUsers.length,
        })}
        message={t('messageConfirmRemovePricingProfileAdminUser', {
          count: pricingProfileAdminUsers.length,
          userName: pricingProfileAdminUsers[0].corporateUser.name,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
